/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, OnDestroy, OnInit, inject, viewChild } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IBoton,
  ICategoria,
  ICliente,
  IConfigPais,
  ILinkMenuMobile,
  ICoordenadas,
  ICreateCliente,
  IListado,
  IQueryParam,
  IReemplazoCategoriaCliente,
  IUsoCategoriaEnCliente,
  TipoCliente,
} from 'modelos/src';
import CONFIGURACIONES_PAIS from 'modelos/src/auxiliares/configuraciones-pais.json';
import { Subscription, firstValueFrom } from 'rxjs';
import { PolygonDrawComponent } from '../../../auxiliares/polygon-draw/polygon-draw.component';
import { HelperService } from '../../../auxiliares/servicios/helper.service';
import { ListadosService } from '../../../auxiliares/servicios/listados.service';
import { TIPOS_CLIENTES } from '../../../../environments/contantes';
import { ClientesService } from '../clientes.service';
import {
  CategoriaEditable,
  crearLinkMenuGroup,
  imagenesCambiaron,
} from './cliente-form.model';
import { ClienteFormService } from './cliente-form.service';

/** Naranja GPE: default de `colorTema` para clientes nuevos (mismo default de la app). */
const COLOR_TEMA_DEFAULT = '#FB972E';

/** Índice de cada pestaña. Lo usan los avisos de "qué falta" para saltar a la correcta. */
const TAB = {
  general: 0,
  categorias: 1,
  appNativa: 2,
  sirenas: 3,
  monitoreo: 4,
  webLegacy: 5,
} as const;

/** Imágenes del set genérico (`assets/clientes-genericos/{control}.png`). */
const IMAGENES_SET_GENERICO = [
  'icono',
  'fondo',
  'sirena',
  'sirenaPush',
  'reflector',
  'reflectorPush',
  'adelante',
  'adelantePush',
  'atras',
  'atrasPush',
];

/**
 * Form ÚNICO de cliente: la misma pantalla para crear (`clientes/crear`) y para editar
 * (`clientes/editar/:id`).
 *
 * Reemplaza a los tres flujos anteriores, que quedaron sin ruta (siguen declarados en el módulo
 * como referencia):
 *  - `crear-cliente-wizard` (stepper, solo podía ELEGIR categorías existentes),
 *  - `provisionador` (solo podía CREAR categorías de cero),
 *  - `crear-editar-cliente` (elegía en la tab General y editaba en la tab App Nativa, los dos
 *    escribiendo los mismos campos de imagen).
 *
 * Diferencias de fondo:
 *  - un solo `crearFormulario()`: en creación el shape es el completo, con defaults, así no hay
 *    campos que solo se puedan cargar después;
 *  - las categorías viven en `CategoriaEditable[]` (única fuente de verdad) y `cliente.categorias`
 *    se deriva al guardar, en una sola pantalla (tab Categorías);
 *  - el guardado (categorías + botones nuevos + cliente, con rollback) lo hace
 *    `ClienteFormService`.
 */
@Component({
  selector: 'app-cliente-form',
  templateUrl: './cliente-form.component.html',
  styleUrls: ['./cliente-form.component.scss'],
  standalone: false,
})
export class ClienteFormComponent implements OnInit, OnDestroy {
  private fb = inject(UntypedFormBuilder);
  private service = inject(ClientesService);
  private formService = inject(ClienteFormService);
  private listadosService = inject(ListadosService);
  private helper = inject(HelperService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  readonly polygonDrawComponent = viewChild(PolygonDrawComponent);

  public formulario?: UntypedFormGroup;
  public loading = false;
  public id: string | null = null;
  public data?: ICliente;

  public tiposCliente: TipoCliente[] = TIPOS_CLIENTES;
  public paisesDisponibles: IConfigPais[] = Object.values(CONFIGURACIONES_PAIS);

  /** Catálogos globales, cargados una sola vez y pasados a las secciones hijas. */
  public categoriasCatalogo: ICategoria[] = [];
  public botonesCatalogo: IBoton[] = [];
  public clientes: ICliente[] = [];
  /** Polígonos de los OTROS clientes, para dibujar el territorio con contexto. */
  public otrosClientes: ICoordenadas[][] = [];
  /** idCategoria → nombres de otros clientes que la usan (badge de alcance). */
  public usoPorCategoria: Record<string, string[]> = {};
  /** idCategoria → cuántos vecinos DE ESTE cliente la tienen, y en qué estado. */
  public usoVecinos: Record<string, IUsoCategoriaEnCliente> = {};
  /**
   * Reemplazos de las categorías que esta edición saca del cliente. Lo llena la sección de
   * categorías y viaja en el PUT: sin esto el backend rechaza la quita con 409.
   */
  public reemplazosCategorias: IReemplazoCategoriaCliente[] = [];

  /** Categorías del cliente. Única fuente de verdad; `cliente.categorias` se deriva al guardar. */
  public categorias: CategoriaEditable[] = [];
  /**
   * Botones de la sección "Servicios o Trámites" del menú "Más" de la app nativa, en orden.
   * Igual que `categorias`: no hay FormArray, se muta in-place y se manda a mano en `getData()`.
   */
  public idsBtnsServiciosTramites: string[] = [];
  /** Se construyen una sola vez: un update por websocket no debe pisar ediciones sin guardar. */
  private categoriasInit = false;
  /** Los tres listados que hacen falta para poder armarlas (aunque vengan vacíos). */
  private listos = { categorias: false, botones: false, clientes: false };

  /** Copiar imágenes de otro cliente (atajo de creación). */
  public idClienteACopiar?: string;

  private datos$?: Subscription;
  private clientes$?: Subscription;
  private categorias$?: Subscription;
  private botones$?: Subscription;

  get esEdicion(): boolean {
    return !!this.id;
  }
  get titulo(): string {
    return this.esEdicion ? 'Editar' : 'Crear';
  }
  get configuracion(): UntypedFormGroup {
    return this.formulario?.get('configuracion') as UntypedFormGroup;
  }
  get imagenes(): UntypedFormGroup {
    return this.formulario?.get('imagenes') as UntypedFormGroup;
  }
  get nombre(): string {
    return this.formulario?.get('nombre')?.value;
  }
  get cantidadImagenesCargadas(): number {
    return Object.values(this.imagenes?.value ?? {}).filter(Boolean).length;
  }

  /** Índice de la pestaña visible. Los avisos de "qué falta" saltan a la que corresponde. */
  public tabSeleccionada = 0;

  public irATab(indice: number): void {
    this.tabSeleccionada = indice;
  }

  /** Controles con validadores, por tab: alimenta el ícono de error del label. */
  public readonly controlesTabs = {
    general: ['nombre', 'tipo', 'pais', 'edadMinima'],
    appNativa: [
      'nombreAppMobile',
      'bundleIdAndroid',
      'bundleIdIOS',
      'configuracion.linksMenuMobile',
    ],
    sirenas: [
      'configuracion.cronLimiteSirenasPor100',
      'configuracion.cronLimiteSirenasMax',
    ],
    monitoreo: ['configuracion.configSOFLEX', 'configuracion.configIRIX'],
  };

  public tabInvalida(controles: string[]): boolean {
    return controles.some((path) => this.formulario?.get(path)?.invalid);
  }

  /** La tab Categorías tiene su propia validación (no todo vive en el form raíz). */
  get categoriasInvalidas(): boolean {
    return (
      !this.categorias.length ||
      !this.formulario?.get('idCategoriaDefault')?.value ||
      this.categorias.some((ed) => ed.grupo.invalid)
    );
  }

  /**
   * Todo lo que impide guardar, en texto y con la pestaña donde se arregla. Alimenta la lista de
   * la barra inferior: si el botón Guardar está deshabilitado, acá se ve exactamente por qué.
   *
   * Tiene que cubrir TODOS los validadores del form: `puedeGuardar` sale de esta lista, así que un
   * bloqueo que no esté enumerado acá deja pasar el click y `enviar()` lo ataja con el aviso
   * genérico. Preferimos eso antes que un botón muerto sin explicación.
   */
  get faltantes(): { texto: string; tab: number }[] {
    const f = this.formulario;
    if (!f) return [];
    const out: { texto: string; tab: number }[] = [];
    const invalido = (path: string): boolean => !!f.get(path)?.invalid;

    if (invalido('nombre')) {
      out.push({ texto: 'Nombre del cliente', tab: TAB.general });
    }
    if (invalido('tipo')) out.push({ texto: 'Tipo', tab: TAB.general });
    if (invalido('pais')) out.push({ texto: 'País', tab: TAB.general });
    if (invalido('edadMinima')) {
      out.push({ texto: 'Edad mínima (entre 1 y 21)', tab: TAB.general });
    }

    if (!this.categorias.length) {
      out.push({ texto: 'Al menos una categoría', tab: TAB.categorias });
    } else {
      if (!f.get('idCategoriaDefault')?.value) {
        out.push({
          texto: 'Marcar la categoría por defecto (estrella de la card)',
          tab: TAB.categorias,
        });
      }
      const incompletas = this.categorias.filter((ed) => ed.grupo.invalid).length;
      if (incompletas) {
        out.push({
          texto: `${incompletas} categoría(s) sin nombre o sin prioridad`,
          tab: TAB.categorias,
        });
      }
    }

    const errorIconoSirena = f.get('imagenes.sirenaMapa')?.errors?.['urlImagen'];
    if (errorIconoSirena) {
      out.push({
        texto: `Ícono de sirena (mapa): ${errorIconoSirena}`,
        tab: TAB.appNativa,
      });
    }
    if (this.tabInvalida(this.controlesTabs.appNativa)) {
      out.push({ texto: 'Datos de la app nativa', tab: TAB.appNativa });
    }
    if (this.tabInvalida(this.controlesTabs.sirenas)) {
      out.push({
        texto: 'Límites de actualización de firmware',
        tab: TAB.sirenas,
      });
    }
    if (this.tabInvalida(this.controlesTabs.monitoreo)) {
      out.push({
        texto: 'Datos de integración (SOFLEX / IRIX)',
        tab: TAB.monitoreo,
      });
    }
    return out;
  }

  get puedeGuardar(): boolean {
    return !this.faltantes.length;
  }

  // ============================ Form ============================

  private crearFormulario(): void {
    const d = this.data;
    const c = d?.configuracion;
    this.formulario = this.fb.group({
      nombre: [d?.nombre, Validators.required],
      tipo: [d?.tipo, Validators.required],
      pais: [d?.pais || 'AR', Validators.required],
      edadMinima: [
        d?.edadMinima,
        [Validators.min(1), Validators.max(21), Validators.required],
      ],
      twitter: [d?.twitter],
      email: [d?.email],
      sirenas: [d?.sirenas],
      porticos: [d?.porticos],
      // Identidad de la app nativa (white-label). Inmutable por build.
      nombreAppMobile: [d?.nombreAppMobile],
      bundleIdAndroid: [d?.bundleIdAndroid],
      bundleIdIOS: [d?.bundleIdIOS],
      // Plantillas white-label del botón nativo.
      loginTemplate: [d?.loginTemplate ?? (this.esEdicion ? null : 'default')],
      inicioTemplate: [d?.inicioTemplate ?? (this.esEdicion ? null : 'grid')],
      configuracion: this.crearConfiguracion(c),
      imagenes: this.crearImagenes(d),
      // Ojo: NO hay FormArray `categorias`. La lista vive en `this.categorias`.
      idCategoriaDefault: [d?.idCategoriaDefault, Validators.required],
      categoriasDefault: this.fb.array([]),
    });
    // Ojo: NO hay FormArray acá tampoco. La lista vive en `this.idsBtnsServiciosTramites`.
    this.idsBtnsServiciosTramites = [...(d?.idsBtnsServiciosTramites ?? [])];

    const porEdad = this.formulario.get(
      'categoriasDefault',
    ) as UntypedFormArray;
    for (const r of d?.categoriasDefault ?? []) {
      porEdad.push(
        this.fb.group({
          desde: [r.desde ?? null],
          hasta: [r.hasta ?? null],
          idCategoria: [r.idCategoria ?? null],
        }),
      );
    }
  }

  private crearConfiguracion(c: any): UntypedFormGroup {
    return this.fb.group({
      direccion: [c?.direccion],
      hostSmartCity: [c?.hostSmartCity],
      // Firmware / sirenas
      actualizacionesFirmware: [c?.actualizacionesFirmware],
      cronLimiteSirenasPor100: [
        c?.cronLimiteSirenasPor100,
        [Validators.min(1), Validators.max(100)],
      ],
      cronLimiteSirenasMax: [c?.cronLimiteSirenasMax, [Validators.min(1)]],
      distanciaCobertura: [c?.distanciaCobertura],
      verSirenasOnline: [c?.verSirenasOnline],
      prioridadSirenas: [c?.prioridadSirenas],
      restringirUsoSirenas: [c?.restringirUsoSirenas],
      tiempoEntreUsoSirenas: [c?.tiempoEntreUsoSirenas],
      cantidadMaxSirenasPorDia: [c?.cantidadMaxSirenasPorDia],
      cantidadMaxSirenasPorMes: [c?.cantidadMaxSirenasPorMes],
      idsClientesACompartirSirenas: [c?.idsClientesACompartirSirenas],
      mostrarSirenasOffline: [c?.mostrarSirenasOffline],
      mostrarEstadoSirenas: [c?.mostrarEstadoSirenas],
      mostrarTodasLasSirenas: [c?.mostrarTodasLasSirenas],
      // Mapa de Cobertura de la app nativa
      iconoSirenaMapa: [c?.iconoSirenaMapa ?? 'default'],
      mostrarLocalidadesCobertura: [c?.mostrarLocalidadesCobertura],
      notificacionesSirenas: [c?.notificacionesSirenas],
      // Monitoreo
      verActualizaciones: [c?.verActualizaciones],
      nombreAppMonit: [c?.nombreAppMonit],
      verSucesos: [c?.verSucesos !== undefined ? c.verSucesos : true],
      reenviarATito: [c?.reenviarATito],
      // Branding del botón nativo
      colorTema: [c?.colorTema ?? (this.esEdicion ? null : COLOR_TEMA_DEFAULT)],
      colorAccent: [c?.colorAccent],
      fuente: [c?.fuente],
      // Colores de la web legacy
      colorInstalar: [c?.colorInstalar],
      colorMenu: [c?.colorMenu],
      colorMenuInferior: [c?.colorMenuInferior],
      colorFondoBotonInferior: [c?.colorFondoBotonInferior],
      colorIconoBotonInferior: [c?.colorIconoBotonInferior],
      colorNavInferior: [c?.colorNavInferior],
      colorNavSuperior: [c?.colorNavSuperior],
      // Instructivo (las imágenes van en imagenes.instructivoN)
      instructivoTexto1: [c?.instructivoTexto1],
      instructivoTexto2: [c?.instructivoTexto2],
      instructivoTexto3: [c?.instructivoTexto3],
      // Pantalla de permisos (imágenes en imagenes.permisoN)
      permisoUbicacionTexto: [c?.permisoUbicacionTexto],
      permisoCamaraTexto: [c?.permisoCamaraTexto],
      permisoMicrofonoTexto: [c?.permisoMicrofonoTexto],
      permisoNotificacionesTexto: [c?.permisoNotificacionesTexto],
      // Módulos del menú inferior
      mostrarMapa: [c?.mostrarMapa],
      verContactos: [c?.verContactos],
      verReclamos: [c?.verReclamos],
      verEventos: [c?.verEventos],
      verEventosExternos: [c?.verEventosExternos],
      verTurnos: [c?.verTurnos],
      verEstacionamientoMedido: [c?.verEstacionamientoMedido],
      verColectivos: [c?.verColectivos],
      verPadron: [c?.verPadron],
      mostrarServiciosTramites: [c?.mostrarServiciosTramites],
      apikeyColectivos: [c?.apikeyColectivos],
      tieneTelemedicina: [c?.tieneTelemedicina],
      mostrarEula: [c?.mostrarEula],
      textoEula: [c?.textoEula],
      // Evento externo
      alertaEventoExterno: [c?.alertaEventoExterno],
      activarSirena: [c?.activarSirena],
      alertaEventoExternoBle: [c?.alertaEventoExternoBle],
      activarSirenaBle: [c?.activarSirenaBle],
      // Control RF
      boton1: [c?.boton1],
      boton2: [c?.boton2],
      boton3: [c?.boton3],
      // Menú mobile
      boton1Mobile: [c?.boton1Mobile],
      boton2Mobile: [c?.boton2Mobile],
      boton3Mobile: [c?.boton3Mobile],
      // Links del menú "Más" de la app nativa (en orden).
      linksMenuMobile: this.fb.array(
        (c?.linksMenuMobile ?? []).map((l: ILinkMenuMobile) => crearLinkMenuGroup(l)),
      ),
      // Integración SOFLEX
      integracionSOFLEX: [c?.integracionSOFLEX],
      configSOFLEX: this.fb.group({
        url: [c?.configSOFLEX?.url],
        user: [c?.configSOFLEX?.user],
        password: [c?.configSOFLEX?.password],
        providerid: [c?.configSOFLEX?.providerid],
        fleetid: [c?.configSOFLEX?.fleetid],
        fleetName: [c?.configSOFLEX?.fleetName],
        maxReintentos: [
          c?.configSOFLEX?.maxReintentos || 3,
          [Validators.min(1), Validators.max(10)],
        ],
        tiempoEntreReintentos: [
          c?.configSOFLEX?.tiempoEntreReintentos || 5000,
          [Validators.min(1000)],
        ],
      }),
      // Integración IRIX
      integracionIRIX: [c?.integracionIRIX],
      configIRIX: this.fb.group({
        url: [c?.configIRIX?.url],
        apikey: [c?.configIRIX?.apikey],
      }),
    });
  }

  private crearImagenes(d?: ICliente): UntypedFormGroup {
    const i = d?.imagenes;
    return this.fb.group({
      icono: [i?.icono],
      lateral: [i?.lateral],
      login: [i?.login],
      fondo: [i?.fondo],
      arriba: [i?.arriba],
      abajo: [i?.abajo],
      sirena: [i?.sirena],
      reflector: [i?.reflector],
      adelante: [i?.adelante],
      atras: [i?.atras],
      sirenaPush: [i?.sirenaPush],
      reflectorPush: [i?.reflectorPush],
      adelantePush: [i?.adelantePush],
      atrasPush: [i?.atrasPush],
      instructivo1: [i?.instructivo1],
      instructivo2: [i?.instructivo2],
      instructivo3: [i?.instructivo3],
      permisoUbicacion: [i?.permisoUbicacion],
      permisoCamara: [i?.permisoCamara],
      permisoMicrofono: [i?.permisoMicrofono],
      permisoNotificaciones: [i?.permisoNotificaciones],
      sirenaMapa: [i?.sirenaMapa],
    });
  }

  /** Si se prende el firmware, los límites de actualización pasan a ser obligatorios. */
  private validadoresFirmware(): void {
    this.configuracion
      .get('actualizacionesFirmware')
      ?.valueChanges.subscribe((val) => {
        const por100 = this.configuracion.controls['cronLimiteSirenasPor100'];
        const max = this.configuracion.controls['cronLimiteSirenasMax'];
        if (val) {
          por100.setValidators([Validators.required]);
          max.setValidators([Validators.required]);
        } else {
          por100.clearValidators();
          max.clearValidators();
        }
        por100.updateValueAndValidity();
        max.updateValueAndValidity();
      });
  }

  // ============================ Categorías ============================

  /**
   * Arma las `CategoriaEditable` del cliente. Se llama una sola vez, cuando ya están los tres
   * insumos (cliente, catálogo de categorías con sus `idsBtns*`, catálogo de botones).
   */
  private construirCategorias(): void {
    if (this.categoriasInit) return;
    if (
      !this.listos.categorias ||
      !this.listos.botones ||
      !this.listos.clientes
    ) {
      return;
    }
    const porId = new Map(this.botonesCatalogo.map((b) => [b._id!, b]));
    const uso = this.usoPorCategoria;
    this.categorias = (this.data?.categorias ?? [])
      .filter((cc) => !!cc.idCategoria)
      .map((cc) =>
        this.formService.desdeCliente(
          cc,
          this.categoriasCatalogo.find((c) => c._id === cc.idCategoria),
          porId,
          uso[cc.idCategoria!] ?? [],
        ),
      );
    this.categoriasInit = true;
  }

  // ============================ Atajos de imágenes ============================

  /** Carga el set genérico GPE en las imágenes base del cliente. */
  public async aplicarSetGenerico(): Promise<void> {
    if (!this.nombre) {
      this.helper.notifWarn('Primero poné el nombre del cliente');
      return;
    }
    this.loading = true;
    try {
      for (const control of IMAGENES_SET_GENERICO) {
        const respuesta = await fetch(
          `assets/clientes-genericos/${control}.png`,
        );
        const blob = await respuesta.blob();
        const file = new File([blob], `${control}.png`, { type: 'image/png' });
        const res = await firstValueFrom(
          this.service.subirImagen(file, this.nombre, control),
        );
        this.imagenes.patchValue({ [control]: res.url });
      }
      this.imagenes.markAsDirty();
      this.helper.notifSuccess('Set genérico aplicado');
    } catch (err) {
      console.error(err);
      this.helper.notifError(err);
    }
    this.loading = false;
  }

  /** Copia las URLs de imagen de otro cliente (solo las que ese cliente tiene cargadas). */
  public copiarImagenesDe(): void {
    const cliente = this.clientes.find((c) => c._id === this.idClienteACopiar);
    if (!cliente?.imagenes) {
      this.helper.notifWarn('El cliente seleccionado no tiene imágenes');
      return;
    }
    const presentes = Object.fromEntries(
      Object.entries(cliente.imagenes).filter(([, url]) => !!url),
    );
    this.imagenes.patchValue(presentes);
    this.imagenes.markAsDirty();
    this.helper.notifSuccess(`Imágenes copiadas de ${cliente.nombre}`);
  }

  public vaciarImagenes(): void {
    this.imagenes.reset();
    this.imagenes.markAsDirty();
    this.helper.notifSuccess('Imágenes vaciadas');
  }

  /** Sube la fuente propia del cliente (OTF/TTF) y guarda la URL en `configuracion.fuente`. */
  public async setFuente(event: Event): Promise<void> {
    const file = (event as any).target?.files?.item(0);
    if (!file) return;
    this.loading = true;
    try {
      const res = await firstValueFrom(
        this.service.subirImagen(file, this.nombre, 'fuente'),
      );
      const fuente = this.configuracion?.get('fuente');
      fuente?.patchValue(res.url + `?${(Math.random() * 1000).toFixed(0)}`);
      fuente?.markAsDirty();
    } catch (err) {
      console.error(err);
      this.helper.notifError(err);
    }
    this.loading = false;
  }

  public selectDireccion(direccion: string): void {
    this.configuracion.patchValue({ direccion });
    this.configuracion.markAsDirty();
  }

  public volver(): void {
    window.history.back();
  }

  // ============================ Guardar ============================

  /**
   * Elimina recursivamente las claves con `null` o `''` para que los campos vacíos del form no
   * viajen al backend: el PUT genérico hace `$set` top-level y un `bundleIdAndroid: null` explícito
   * pisa el valor guardado (omitido, en cambio, lo preserva). Preserva `false`, `0` y arrays.
   */
  private limpiarVacios<T>(valor: T): T {
    if (Array.isArray(valor)) {
      return valor.map((v) => this.limpiarVacios(v)) as T;
    }
    if (valor && typeof valor === 'object') {
      const limpio: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(valor)) {
        if (v === null || v === '') continue;
        limpio[k] = this.limpiarVacios(v);
      }
      return limpio as T;
    }
    return valor;
  }

  private getData(): ICreateCliente {
    const data: ICreateCliente = this.limpiarVacios(this.formulario?.value);
    data.coordenadas = this.polygonDrawComponent()?.polygons || [];
    data.superficie = this.helper.calcularAreas(data.coordenadas);
    data.idsBtnsServiciosTramites = this.idsBtnsServiciosTramites;
    return data;
  }

  /**
   * ¿El cliente en sí quedó sin cambios? El form raíz y el polígono (que no pasa por el form).
   * La parte de categorías la evalúa `ClienteFormService`, que es quien la arma.
   */
  private clientePristine(): boolean {
    if (!this.formulario?.pristine) return false;
    if (this.categorias.some((ed) => imagenesCambiaron(ed))) return false;
    if (
      JSON.stringify(this.idsBtnsServiciosTramites) !==
      JSON.stringify(this.data?.idsBtnsServiciosTramites ?? [])
    ) {
      return false;
    }
    const coordenadas = this.polygonDrawComponent()?.polygons || [];
    return (
      JSON.stringify(coordenadas) ===
      JSON.stringify(this.data?.coordenadas || [])
    );
  }

  /**
   * Reapunta los ids de categoría del form (default y rangos por edad) a los ids reales. Los
   * `CategoriaEditable` los reapunta el servicio; esto cierra la parte que vive en el form.
   */
  private reapuntarIdsEnForm(mapaCat: Map<string, string>): void {
    const def = this.formulario?.get('idCategoriaDefault');
    const real = mapaCat.get(def?.value);
    if (real && real !== def?.value) {
      def?.patchValue(real);
    }
    const porEdad = this.formulario?.get(
      'categoriasDefault',
    ) as UntypedFormArray;
    for (const fila of porEdad?.controls ?? []) {
      const idReal = mapaCat.get(fila.get('idCategoria')?.value);
      if (idReal && idReal !== fila.get('idCategoria')?.value) {
        fila.patchValue({ idCategoria: idReal });
      }
    }
  }

  /** Deja todo como recién guardado, para que el próximo Guardar pueda saltear el PUT. */
  private marcarComoGuardado(): void {
    this.formulario?.markAsPristine();
    for (const ed of this.categorias) {
      ed.imagenes.markAsPristine();
      ed.imagenesLegacy.markAsPristine();
      ed.grupo.markAsPristine();
    }
  }

  public async enviar(): Promise<void> {
    // Con tabs, un campo inválido puede estar oculto: marcar todo y avisar.
    if (this.formulario?.invalid || this.categoriasInvalidas) {
      this.formulario?.markAllAsTouched();
      this.categorias.forEach((ed) => ed.grupo.markAllAsTouched());
      const faltan = this.faltantes.map((f) => f.texto).join(', ');
      this.helper.notifError(
        faltan
          ? `Falta completar: ${faltan}`
          : 'Hay campos inválidos: revisá las pestañas marcadas con error',
      );
      if (this.faltantes.length) {
        this.irATab(this.faltantes[0].tab);
      }
      return;
    }
    this.loading = true;
    try {
      const { cliente, mapaCat } = await this.formService.guardar({
        idCliente: this.data?._id,
        original: this.data,
        datos: this.getData(),
        categorias: this.categorias,
        idCategoriaDefault: this.formulario?.get('idCategoriaDefault')?.value,
        categoriasDefault:
          this.formulario?.get('categoriasDefault')?.value ?? [],
        clientePristine: this.clientePristine(),
        reemplazosCategorias: this.reemplazosCategorias,
      });
      // Un guardado exitoso ya reasignó: si no se limpia, un segundo guardado mandaría
      // reemplazos de categorías que el cliente ya no tiene y el backend los rechazaría.
      this.reemplazosCategorias = [];
      // Los ids temporales (`tmp-cat-N`) del form pasan a los reales que devolvió el backend.
      this.reapuntarIdsEnForm(mapaCat);
      this.helper.notifSuccess('Guardado correctamente');
      if (!this.data?._id) {
        // Recién creado: se pasa al mismo form pero en modo edición, con los ids reales.
        await this.router.navigate(['main', 'clientes', 'editar', cliente._id]);
        return;
      }
      this.marcarComoGuardado();
    } catch (err) {
      console.error(err);
      this.helper.notifError(err);
    }
    this.loading = false;
  }

  // ============================ Listados ============================

  private async listar(id: string): Promise<void> {
    this.datos$?.unsubscribe();
    this.datos$ = this.listadosService
      .subscribe<ICliente>('cliente', id)
      .subscribe((data) => {
        this.data = data;
        console.debug(new Date().toLocaleString(), `listado de cliente`, data);
      });
    await this.listadosService.getLastValue('cliente', id);
  }

  /** Clientes: polígonos de contexto, copiar imágenes, y el uso de cada categoría. */
  private async listarClientes(): Promise<void> {
    const query: IQueryParam = {
      sort: 'nombre',
      select: 'nombre coordenadas categorias imagenes',
    };
    this.clientes$?.unsubscribe();
    this.clientes$ = this.listadosService
      .subscribe<IListado<ICliente>>('clientes', query)
      .subscribe((data) => {
        this.clientes = data.datos;
        this.otrosClientes = [];
        for (const cliente of this.clientes) {
          if (cliente._id !== this.id && cliente.coordenadas?.length) {
            this.otrosClientes.push(...cliente.coordenadas);
          }
        }
        this.usoPorCategoria = this.formService.usoPorCategoria(
          this.clientes,
          this.id ?? undefined,
        );
        this.listos.clientes = true;
        this.construirCategorias();
      });
    await this.listadosService.getLastValue('clientes', query);
  }

  private async listarCategorias(): Promise<void> {
    const query: IQueryParam = {
      sort: 'nombre',
      select:
        'nombre variante inicioTemplate plantillaBotonera nombreTemplate prioridad color idsBtnsPrincipales idsBtnsSecundarios idsOtrosBotones',
    };
    this.categorias$?.unsubscribe();
    this.categorias$ = this.listadosService
      .subscribe<IListado<ICategoria>>('categorias', query)
      .subscribe((data) => {
        this.categoriasCatalogo = data.datos;
        this.listos.categorias = true;
        this.construirCategorias();
      });
    await this.listadosService.getLastValue('categorias', query);
  }

  /**
   * Catálogo COMPLETO de botones (no `botonesPorCliente`): las categorías pueden referenciar
   * botones que no son de este cliente, y con el listado por cliente quedaban sin nombre.
   */
  private async listarBotones(): Promise<void> {
    const query: IQueryParam = { sort: 'nombre' };
    this.botones$?.unsubscribe();
    this.botones$ = this.listadosService
      .subscribe<IListado<IBoton>>('botones', query)
      .subscribe((data) => {
        this.botonesCatalogo = data.datos;
        this.listos.botones = true;
        this.construirCategorias();
      });
    await this.listadosService.getLastValue('botones', query);
  }

  async ngOnInit(): Promise<void> {
    const params = await firstValueFrom(this.route.paramMap);
    this.id = params.get('id');
    if (this.id) {
      await this.listar(this.id);
    }
    this.crearFormulario();
    this.validadoresFirmware();
    await Promise.all([
      this.listarClientes(),
      this.listarCategorias(),
      this.listarBotones(),
      this.cargarUsoVecinos(),
    ]);
  }

  /**
   * Cuántos vecinos tiene cada categoría de este cliente. Se pide una sola vez y de una sola
   * llamada: decide si al quitar una categoría hay que pedir reemplazo, y con qué números.
   */
  private async cargarUsoVecinos(): Promise<void> {
    if (!this.id) return;
    try {
      const uso = await firstValueFrom(this.service.usoCategorias(this.id));
      const indice: Record<string, IUsoCategoriaEnCliente> = {};
      for (const categoria of uso.categorias ?? []) {
        indice[categoria.idCategoria] = categoria;
      }
      this.usoVecinos = indice;
    } catch (err) {
      // No es bloqueante: sin el conteo el backend igual rechaza una quita sin reemplazo, sólo
      // que el aviso llega recién al guardar en vez de al momento de quitarla.
      console.error('No se pudo cargar el uso de categorías', err);
    }
  }

  ngOnDestroy(): void {
    this.datos$?.unsubscribe();
    this.clientes$?.unsubscribe();
    this.categorias$?.unsubscribe();
    this.botones$?.unsubscribe();
  }
}
